import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChatSendMessage } from '~/hooks/use-agent-chat';
import { ArrowDown } from '@phosphor-icons/react';
import type { UIMessage } from 'ai';
import { MessageBubble } from './message-bubble';
import { AnswerSkeleton } from './answer-skeleton';
import { PendingApprovalCards } from './pending-approval-card';
import { shouldShowJumpPill } from './jump-pill-logic';
import { useApprovalFollow } from './use-approval-follow';
import { BudgetBlockedCard } from '~/features/limits/budget-blocked-card';
import { isComposingAfterTool } from './compose-skeleton-logic';
import { assistantHasNoVisibleContent } from './assistant-waiting';
import { useHiddenMessages } from '~/lib/stores/hidden-messages-store';
import { Button } from '~/components/ui/button';
import type { BranchInfo } from '~/hooks/queries/use-branches';
import { renderKeyOf } from '~/hooks/reconcile-history';
import { useChatStatuses } from '~/hooks/queries/use-chat-status';
import { isTurnActive } from './turn-active-logic';
import { bottomScrollTop, OPEN_SETTLE_MS, pickScroller, shouldRepinOnGrowth } from './open-at-bottom-logic';
import { useUiRefactoring } from '~/lib/ui-refactoring';

/** The ancestor that scrolls the conversation; see `pickScroller`. */
function findScroller(root: HTMLElement): HTMLElement | null {
  const chain: HTMLElement[] = [];
  for (let el = root.parentElement; el; el = el.parentElement) chain.push(el);
  const index = pickScroller(
    chain.map((el) => {
      const style = window.getComputedStyle(el);
      return { overflowY: style.overflowY, position: style.position, scrollHeight: el.scrollHeight, clientHeight: el.clientHeight };
    }),
  );
  return index === -1 ? null : chain[index];
}

interface ChatMessagesProps {
  messages: UIMessage[];
  status: string;
  activeBranchId?: string;
  onRetry?: (input: { id?: string | number; content?: string }) => void | Promise<void>;
  onDelete?: (input: { id?: string | number; role?: string; content?: string }) => void | Promise<void>;
  /** Threaded into PlanApprovalCard so Approve/Reject can fire a follow-up
   *  turn. The pending-approval cards send none (FRONT-3036). */
  onSendMessage?: ChatSendMessage;
  /**
   * 2026-07-21 (FRONT-907): starts a live voice call on this chat's own
   * session — threaded down to a `quick-replies` option with `action: "call"`
   * (the day-one interview offer's "Let's talk").
   */
  onStartCall?: () => void;
  /**
   * Session id of the chat surface (the zen modal / launcher run under a
   * per-surface `web:`/`inbox-` id, FRONT-518/522). Threaded into each
   * MessageBubble → QuestionsCard so session-scoped cards query + mutate the
   * right session instead of the global one. Required so the wiring can't be
   * silently dropped (the FRONT-540 regression). (FRONT-540)
   */
  sessionId: string;
  /** Anchor-message-id → ordered list of sibling branches for the fork nav. */
  forkGroups?: Map<number, BranchInfo[]>;
  /** Switch the active branch when the user clicks a sibling-nav arrow. */
  onSwitchBranch?: (branchId: string) => void;
  /** Summarize-and-fold the current sibling branch into its parent. */
  onMergeBranch?: (branchId: string) => void | Promise<void>;
  /** Branch id currently being merged (drives the spinner). */
  mergingBranchId?: string | null;
  /** When set, assistant messages show a "Save as document" action. */
  onSaveAsDocument?: (text: string) => void;
  /**
   * When true, threaded into every MessageBubble so the Eddy Stone
   * identity-influence badge shows its tooltip but isn't clickable — set by
   * the day-one chat, whose pre-app shell has no `/settings` route to land
   * on (Marko, 2026-07-29 live-test).
   */
  disableIdentityLink?: boolean;
  /**
   * FRONT-221: true while a turn for this session is still running on the server
   * with no live stream attached here (e.g. the inbox modal was reopened after
   * the user left mid-run). Keeps the last user message's action chip in its
   * "…in progress" state instead of prematurely settling to the done state.
   */
  backgroundActive?: boolean;
  /**
   * Callback fired whenever the rendered bubble positions change (mount,
   * stream-grow, window resize, etc). The branch graph rail consumes
   * this to align its dots with the actual message rows.
   */
  onLayout?: (layout: { positions: Map<number, number>; height: number }) => void;
}

// Pixel slack from the bottom that still counts as "pinned to bottom". A user
// who's a few px off the floor (because of font rendering / margin rounding)
// shouldn't unstick the auto-scroll.
const STICK_THRESHOLD_PX = 80;

// How long the positional scroll listener is prevented from RE-PINNING
// (never un-pinning — see onScrollerScroll) after a scroll we know wasn't the
// user asking to resume following: our own scrollToBottom call (and its
// smooth-scroll animation frames), an upward-intent un-pin (so the gesture's
// own scroll event can't undo itself), or a message-identity churn (the
// post-stream history reseed swaps every temp id for a real DB id, which
// remounts every bubble under new keys and can transiently clamp scrollTop to
// a momentarily smaller max). (FRONT-842)
const SUPPRESS_REPIN_MS = 300;

export function ChatMessages({
  messages,
  status,
  activeBranchId,
  onRetry,
  onDelete,
  onSendMessage,
  onStartCall,
  sessionId,
  forkGroups,
  onSwitchBranch,
  onMergeBranch,
  mergingBranchId,
  onSaveAsDocument,
  backgroundActive,
  onLayout,
  disableIdentityLink,
}: ChatMessagesProps) {
  // FRONT-3223 (eddy, 2026-10-28): clearer spacing — more air between turns.
  const roomier = useUiRefactoring();
  const hiddenIds = useHiddenMessages((s) => s.ids);
  const visibleMessages = messages.filter((m) => !m.id || !hiddenIds.has(m.id));
  // Shared identity key for the visible list — reused by the layout effect
  // below AND the id-reseed detection further down, so it's computed once
  // per render rather than twice. (FRONT-842)
  const idsKey = visibleMessages.map((m) => m.id).join('|');
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const bubbleRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Measure each bubble's Y position relative to our container and bubble
  // them up so the branch-graph rail can draw dots aligned to the
  // actual message rows. Re-runs on every layout-affecting change
  // (mount, stream-grow, image load, window resize) via ResizeObserver.
  useLayoutEffect(() => {
    if (!onLayout) return;
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const containerTop = container.getBoundingClientRect().top;
      const positions = new Map<number, number>();
      bubbleRefs.current.forEach((el, id) => {
        const r = el.getBoundingClientRect();
        positions.set(id, r.top - containerTop + r.height / 2);
      });
      onLayout({ positions, height: container.scrollHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    bubbleRefs.current.forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, [onLayout, visibleMessages.length, idsKey]);

  const isStreaming = status === 'streaming';

  // The "thinking" indicator must stay visible from send until the assistant
  // actually has something to show. status flips to 'streaming' as soon as
  // the SSE connection opens — well before any token/tool/reasoning lands —
  // so 'submitted' alone disappears too quickly. We also keep it on while
  // streaming if the latest assistant message has no visible parts yet.
  // FRONT-2036 fix round 2 (N1): `lastAssistantHasNoVisibleContent` is now
  // the shared `assistantHasNoVisibleContent` (`assistant-waiting.ts`) — the
  // phone surface (`pwa-shell.tsx`) needed the exact same rule and used to
  // have none, which showed a blank feed for this whole window on every
  // turn. Behaviour here is byte-identical to before the extraction.
  const lastVisibleMessage = messages[messages.length - 1];
  const lastAssistantHasNoVisibleContent = assistantHasNoVisibleContent(lastVisibleMessage);
  const isWaiting =
    status === 'submitted' ||
    (isStreaming && (lastVisibleMessage?.role !== 'assistant' || lastAssistantHasNoVisibleContent));

  // The "compose gap" (FRONT-855 Task 3): once a tool call finishes, there's
  // a real pause before the model streams the answer text. isWaiting above
  // is false by then (a tool part counts as visible content), so that gap
  // showed nothing. isComposingAfterTool covers exactly that window and is
  // mutually exclusive with isWaiting by construction — see
  // compose-skeleton-logic.ts for why the two shapes can't both be true.
  const isComposing =
    lastVisibleMessage?.role === 'assistant' && isComposingAfterTool(status, lastVisibleMessage);

  // Auto-scroll is paused as soon as the user scrolls up while streaming.
  // Re-pinned when they hit the "Jump to latest" pill or send a new message.
  const [autoScroll, setAutoScroll] = useState(true);
  // Ref mirror of autoScroll, synced SYNCHRONOUSLY at every write via
  // setPinned: the message-count effect below is a layout effect keyed only on
  // the count, so it must read the current pin state without a stale closure —
  // and a passive sync effect would lag one commit behind when an un-pin and a
  // new message land in the same batch. (FRONT-246)
  const autoScrollRef = useRef(true);
  const setPinned = useCallback((pinned: boolean) => {
    autoScrollRef.current = pinned;
    setAutoScroll(pinned);
  }, []);
  // Epoch ms until which the positional listener may not RE-PIN. Set by
  // suppressPositionalRepin(); read by onScrollerScroll. (FRONT-842)
  const suppressRepinUntilRef = useRef(0);
  const suppressPositionalRepin = useCallback(() => {
    suppressRepinUntilRef.current = Date.now() + SUPPRESS_REPIN_MS;
  }, []);
  // Epoch ms until which the positional listener is ignored ENTIRELY (not
  // just gated on re-pin) — armed only around our own scrollToBottom call.
  // A scrollToBottom() started from FAR above the stick band (e.g. sending a
  // message while scrolled way up) animates through an out-of-band state on
  // its way to the target; the un-pin branch below is deliberately
  // unconditional (scrollbar drag / PageUp have no other un-pin signal), so
  // without this guard the animation's own early frames read as a genuine
  // "user scrolled away" and silently undo the pin the count effect just set
  // — landing the user at the bottom but un-pinned, so the reply stops
  // following. Self-extends like suppressRepinUntilRef, for the same reason
  // (a long-distance smooth scroll can outlast a one-shot deadline).
  // (FRONT-842, caught in review)
  const ignorePositionalUntilRef = useRef(0);
  const scrollToBottom = useCallback((opts?: ScrollIntoViewOptions) => {
    suppressPositionalRepin();
    ignorePositionalUntilRef.current = Date.now() + SUPPRESS_REPIN_MS;
    // Scroll only the conversation's own scroller, never `scrollIntoView`: that
    // walks every scrollable ancestor, and a chat window opened from the Chats
    // list sits inside the list's scroller, so opening a chat scrolled the
    // list behind it to its end. The first snap runs before the listener
    // effect has found the scroller, so look it up here too.
    // FRONT-3142: the real maximum, not the sentinel, which sits inside the
    // column's bottom padding and never reaches the true end.
    const root = containerRef.current;
    const scroller = scrollerRef.current ?? (root ? findScroller(root) : null);
    if (scroller) scroller.scrollTo({ top: bottomScrollTop(scroller), behavior: opts?.behavior ?? 'smooth' });
    else bottomRef.current?.scrollIntoView(opts ?? { behavior: 'smooth' });
  }, [suppressPositionalRepin]);

  const lastMessage = visibleMessages.at(-1);
  const streamingText =
    isStreaming && lastMessage?.role === 'assistant'
      ? lastMessage.parts
          .filter((p) => p.type === 'text')
          .map((p) => p.text)
          .join('')
      : '';

  // The scroll container is somewhere up the tree (the route's overflow-y).
  //
  // FRONT-246 (round 2): two flaws made the original "pause on scroll-up"
  // mechanism unreliable in practice:
  //
  // 1. The scroller walk ran once on mount and required the ancestor to be
  //    ALREADY overflowing (scrollHeight > clientHeight). The modal mounts
  //    before history loads / before a stream grows past the viewport, so the
  //    walk could find nothing and the scroll listener never attached —
  //    auto-follow was then impossible to pause. The walk now retries on
  //    every render until a scroller is acquired (a few getComputedStyle
  //    calls per attempt, none once acquired).
  //
  // 2. Un-pinning relied on the achieved scroll POSITION crossing
  //    STICK_THRESHOLD_PX. Each streamed token smooth-scrolls back to the
  //    bottom, so a human scrolling up in small trackpad increments (each
  //    tick smaller than the threshold) was reeled back in before ever
  //    crossing it — a tug-of-war only a single large jump could win. Upward
  //    INTENT (wheel or touch drag, handlers on the root div below) now
  //    un-pins directly; the positional listener still un-pins unconditionally
  //    on a large jump (scrollbar drags, PageUp — there's no other signal for
  //    those), and still re-pins at the bottom, but see onScrollerScroll below:
  //    re-pinning from position is debounced against non-user-caused scrolls
  //    (round 3, FRONT-842).
  const scrollerRef = useRef<HTMLElement | null>(null);
  const onScrollerScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const now = Date.now();
    // A scrollToBottom() animation may still be in flight and can pass
    // through an out-of-band state on its way to the target — ignore the
    // listener entirely (neither direction) until it's been quiet for a
    // beat. See ignorePositionalUntilRef above. (FRONT-842)
    if (now < ignorePositionalUntilRef.current) {
      ignorePositionalUntilRef.current = now + SUPPRESS_REPIN_MS;
      return;
    }
    const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    const withinStickBand = distanceFromBottom <= STICK_THRESHOLD_PX;
    // Un-pinning from position (large jumps: scrollbar drag, PageUp) always
    // goes through — those have no other signal to un-pin from. Re-pinning
    // from position is suppressed for a short window after a scroll we know
    // wasn't the user asking to resume following (see suppressPositionalRepin
    // call sites). A fixed-duration window alone isn't enough: trackpad
    // momentum scrolling (or a remount still settling) can keep producing
    // in-band scroll events past a one-shot deadline, letting a stray late
    // event re-pin the user anyway — exactly the bug this is fixing. So each
    // in-band event arriving WHILE still inside an active window extends it
    // (debounce, not a one-shot timer); only once the window has gone truly
    // quiet for SUPPRESS_REPIN_MS does the NEXT scroll — a genuinely new one
    // — get to decide pin state again. (FRONT-842)
    if (withinStickBand && now < suppressRepinUntilRef.current) {
      suppressRepinUntilRef.current = now + SUPPRESS_REPIN_MS;
      return;
    }
    setPinned(withinStickBand);
  }, [setPinned]);
  useEffect(() => {
    if (scrollerRef.current) return;
    const root = containerRef.current;
    if (!root) return;
    // Prefer an ancestor that is scrollable AND already overflowing; fall back
    // to the nearest one with a scrollable overflow-y style. The modal's
    // scroll column exists before any content overflows it — requiring
    // overflow at attach time is what kept the listener from ever attaching,
    // and retrying the walk forever on a never-overflowing chat is the cost
    // the fallback avoids.
    const scroller = findScroller(root);
    if (!scroller) return;
    scrollerRef.current = scroller;
    scroller.addEventListener('scroll', onScrollerScroll, { passive: true });
  }, [visibleMessages.length, streamingText, onScrollerScroll]);
  useEffect(
    () => () => {
      // Null the ref too: under StrictMode's dev double-invoke this cleanup
      // runs between the two mounts, and a surviving ref would make the
      // acquisition effect bail with the listener permanently detached.
      scrollerRef.current?.removeEventListener('scroll', onScrollerScroll);
      scrollerRef.current = null;
    },
    [onScrollerScroll],
  );

  // A wheel-up or upward touch drag anywhere over the list pauses auto-follow
  // immediately — unless the gesture is aimed at a nested scrollable region
  // (tool cards cap their output with their own overflow-y) that can still
  // scroll up itself: the browser scrolls that region, the chat view never
  // moves, and un-pinning would silently stop auto-follow for no visible
  // reason. Mirrors native scroll chaining: once the nested region is at its
  // top, further upward intent belongs to the chat again.
  const unpinOnUpwardIntent = useCallback(
    (target: EventTarget | null) => {
      let el = target instanceof HTMLElement ? target : null;
      const root = containerRef.current;
      while (el && el !== root) {
        if (el.scrollTop > 0 && el.scrollHeight > el.clientHeight) {
          const overflowY = window.getComputedStyle(el).overflowY;
          if (overflowY === 'auto' || overflowY === 'scroll') return;
        }
        el = el.parentElement;
      }
      setPinned(false);
      // Keep the positional listener from re-pinning us on the very scroll
      // event this gesture just produced (or the next few ticks of the same
      // continuous gesture) while it's still within the stick band — that
      // race is what only let a single HARD scroll escape before. (FRONT-842)
      suppressPositionalRepin();
    },
    [setPinned, suppressPositionalRepin],
  );
  const touchStartYRef = useRef<number | null>(null);

  // First paint in this mounted instance: snap to bottom instantly and
  // pre-paint, so the user never sees a flashed-from-top scroll when
  // navigating back into the chat.
  //
  // FRONT-246: on every later message-count change we used to re-pin
  // unconditionally (setAutoScroll(true) + scroll to bottom). That yanked a
  // user who'd scrolled up back to the floor whenever a new message landed —
  // and an agentic turn lands several (tool-call + result + follow-up). Now we
  // only force the jump when the new message is the user's OWN send (they
  // expect to land at the bottom). When an assistant/tool message arrives we
  // respect their scroll position: follow it down only if they're still pinned,
  // so reading back through the conversation mid-stream is never interrupted.
  const didInitialScrollRef = useRef(false);
  const prevCountRef = useRef(0);
  // FRONT-3142: epoch ms until which late content growth (markdown, images,
  // fonts, footers) re-pins a still-pinned reader to the floor.
  const openSettleUntilRef = useRef(0);
  useLayoutEffect(() => {
    const prevCount = prevCountRef.current;
    prevCountRef.current = visibleMessages.length;
    if (visibleMessages.length === 0) return;
    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      setPinned(true);
      scrollToBottom({ behavior: 'auto', block: 'end' });
      openSettleUntilRef.current = Date.now() + OPEN_SETTLE_MS;
      return;
    }
    // Force-jump only when a NEW message landed and it is the user's own send.
    // A shrinking list (hide/delete) that happens to leave a user message last
    // is not a send and must not steal the reading position. (FRONT-246)
    const grew = visibleMessages.length > prevCount;
    const lastIsUser = visibleMessages.at(-1)?.role === 'user';
    if (grew && lastIsUser) {
      setPinned(true);
      scrollToBottom();
    } else if (autoScrollRef.current) {
      scrollToBottom();
    }
  }, [visibleMessages.length]);

  // FRONT-3142: content that settles after the first snap grows the column
  // with nothing to re-scroll, leaving the window a little above the bottom.
  // Re-pin on every size change of the list, but only inside the open settle
  // window and only while the reader has not scrolled away.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (
        !shouldRepinOnGrowth({
          now: Date.now(),
          settleUntil: openSettleUntilRef.current,
          pinned: autoScrollRef.current,
        })
      ) {
        return;
      }
      const scroller = scrollerRef.current;
      if (!scroller) return;
      suppressPositionalRepin();
      ignorePositionalUntilRef.current = Date.now() + SUPPRESS_REPIN_MS;
      scroller.scrollTop = bottomScrollTop(scroller);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [suppressPositionalRepin]);

  // FRONT-842: the post-stream history refresh (onFinish in use-agent-chat.ts)
  // swaps every message's synthetic id for its real DB id — same count, all
  // new ids — which changes every list key below and remounts every bubble.
  // If the remounted content is transiently a different height, the browser
  // clamps scrollTop to the new max, landing an un-pinned reader back at the
  // floor. Capture the scroller's position DURING render, before this
  // render's commit remounts the bubbles — reading scrollTop here still sees
  // the OLD, pre-remount DOM — and restore it in the layout effect below,
  // once the new DOM has committed but before the browser paints.
  const prevIdsKeyRef = useRef(idsKey);
  const pendingScrollRestoreRef = useRef<number | null>(null);
  if (prevIdsKeyRef.current !== idsKey) {
    prevIdsKeyRef.current = idsKey;
    // Only an identity-only churn (same message COUNT, different ids — the
    // reseed) risks this clamp. An ordinary new-message arrival (count grew)
    // is already handled by the count effect above and must NOT also arm
    // this: during a busy agentic turn appending several messages in quick
    // succession, re-arming on every arrival would starve the "user scrolls
    // back down to resume following" re-pin signal for the whole turn.
    if (visibleMessages.length === prevCountRef.current) {
      pendingScrollRestoreRef.current = scrollerRef.current?.scrollTop ?? null;
    }
  }
  useLayoutEffect(() => {
    const restoreScrollTop = pendingScrollRestoreRef.current;
    pendingScrollRestoreRef.current = null;
    if (restoreScrollTop == null) return;
    // Pinned readers should keep following to the bottom as normal — this is
    // only for an un-pinned reader whose position the remount just clamped.
    if (autoScrollRef.current) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTop = restoreScrollTop;
    suppressPositionalRepin();
  }, [idsKey, suppressPositionalRepin]);

  // Streaming-text deltas + waiting pulse → only scroll if pinned.
  useEffect(() => {
    if (!autoScroll) return;
    scrollToBottom();
  }, [streamingText, isWaiting, autoScroll, scrollToBottom]);

  // BUG-071 / FRONT-1311: keep an arriving approval in view. Behaviour lives in
  // the hook — see use-approval-follow.ts for why an approval needs its own
  // follow and why the scroll there is instant rather than smooth.
  const pendingApprovalCount = useApprovalFollow({ sessionId, autoScroll, scrollToBottom });

  const showJumpPill = shouldShowJumpPill({
    autoScroll,
    isStreaming,
    isWaiting,
    pendingApprovalCount,
  });

  // FRONT-199/221: a button-action user bubble shows a spinner while its turn is
  // still working and settles once done. "Working" = it's the latest user
  // message and the turn is active — either a live local stream
  // (submitted/streaming) OR a server-side run with no live stream attached here
  // (backgroundActive, e.g. the inbox modal was reopened mid-run). Without the
  // backgroundActive arm the chip would prematurely flip to its green "done"
  // state on reopen even though the server is still rewriting. See
  // turn-active-logic.ts — this is also the exact signal that drives the
  // chat-status poll just below, so the two cannot disagree.
  const turnActive = isTurnActive(status, backgroundActive);
  // FRONT-2343: every surface that renders ChatMessages gets the 2s
  // chat-status poll (and the cross-replica approval reconcile riding on
  // it — see use-chat-status.ts's `chatStatusesQueryOptions` docblock) for
  // free, keyed off the same `turnActive` as the chip above. This is the
  // ONE call site for that poll now — a chat surface that mounts
  // ChatMessages without also calling useChatStatuses itself used to miss a
  // lost cross-replica approval push entirely (the Eddy Machine desktop
  // operator panel's Chat tab did, live-repro'd 0/3 in 45s before this
  // moved here). Guarded against an empty sessionId the same way
  // useChatStatuses itself guards a request with no ids: pass none rather
  // than `['']`, so `enabled` stays false instead of polling a query that
  // can never resolve to anything.
  useChatStatuses(sessionId ? [sessionId] : [], { pollWhile: turnActive });
  const lastUserMessageId = [...visibleMessages].reverse().find((m) => m.role === 'user')?.id;

  // FRONT-847: text of the nearest preceding user message for each rendered
  // bubble — threaded down to SignalCards so ConnectIntegrationCard's Retry
  // can re-send the original request verbatim once the provider connects.
  // Assistant turns can span several messages (tool calls, follow-ups); the
  // "original request" is always the last thing the user actually typed
  // before this turn started.
  const textOf = (msg: UIMessage): string =>
    msg.parts
      .filter((p) => p.type === 'text')
      .map((p) => (p as { type: 'text'; text: string }).text)
      .join('');

  return (
    <div
      ref={containerRef}
      data-testid="chat-messages"
      className={roomier ? 'flex flex-col gap-6' : 'flex flex-col gap-3'}
      onWheel={(e) => {
        // FRONT-246: upward intent pauses auto-follow immediately — waiting
        // for the position to cross STICK_THRESHOLD_PX loses the race against
        // the per-token scroll for small trackpad increments.
        if (e.deltaY < 0) unpinOnUpwardIntent(e.target);
      }}
      onTouchStart={(e) => {
        touchStartYRef.current = e.touches[0]?.clientY ?? null;
      }}
      onTouchMove={(e) => {
        // Touch drags never fire wheel events; a finger moving DOWN scrolls
        // the content up. 8px of slop so a wobbly tap doesn't un-pin.
        const startY = touchStartYRef.current;
        const y = e.touches[0]?.clientY;
        if (startY == null || y == null) return;
        if (y > startY + 8) unpinOnUpwardIntent(e.target);
      }}
    >
      {visibleMessages.map((msg, i) => {
        const isLast = i === visibleMessages.length - 1;
        const showStreaming = isLast && isStreaming && msg.role === 'assistant';
        // FRONT-2387 fix round 4 (#8): the play-answer control's own
        // streaming gate is wider than `showStreaming` above — it must also
        // hide on a turn that's running on the SERVER with no live stream
        // attached HERE (`backgroundActive`, e.g. this chat surface was
        // reopened mid-run and the last assistant message on screen is a
        // partial answer from before the reload). `turnActive` is the exact
        // same signal already driving the "…in progress" chip and the
        // chat-status poll above — reusing it here rather than adding a
        // second, differently-scoped notion of "active". Still gated to the
        // LAST assistant message only: an older, already-finished answer
        // keeps its control regardless of what the current turn is doing.
        const audioAnswerStreaming = isLast && turnActive && msg.role === 'assistant';
        const actionRunning =
          turnActive && msg.role === 'user' && msg.id != null && msg.id === lastUserMessageId;
        const numericId =
          typeof msg.id === 'string' ? parseInt(msg.id, 10) : (msg.id as unknown as number);
        let originalUserText: string | undefined;
        for (let j = i - 1; j >= 0; j--) {
          if (visibleMessages[j].role === 'user') {
            originalUserText = textOf(visibleMessages[j]);
            break;
          }
        }
        return (
          <div
            // FRONT-1685 tried a positional key here, to stop the post-stream
            // synthetic-to-real id swap tearing down the QuestionsCard's
            // draft. Reverted: identity must NOT be positional. The list is
            // not append-only — a branch switch replaces its tail, and hiding
            // or deleting a message shifts every index below it — so a
            // positional key hands one message's mounted subtree to a
            // DIFFERENT message. Descendants holding mount-captured state
            // then render the previous message's data: ReasoningBlock's
            // `startedAt`/`finishedAt` refs would show the wrong "Thought for
            // Xs", carrying its expanded state over with it.
            //
            // Neither RAW key is safe, which is the point: `message.id`
            // breaks on the id swap, position breaks on a length change, and
            // the single reseed at turn settle does both.
            //
            // FRONT-1717 PR 2 resolved that by SUPPLYING a stable key instead
            // of choosing between those two. `renderKeyOf` reads the render
            // identity the reconcile carries across the id promotion (see
            // `reconcile-history.ts`), falling back to the id when there is
            // none — stable across the settle without being positional.
            // `msg.id` is untouched and is still the persisted row id for
            // every other consumer, including the hidden-message filter and
            // `idsKey` above.
            key={renderKeyOf(msg) ?? i}
            ref={(el) => {
              if (!Number.isFinite(numericId)) return;
              if (el) bubbleRefs.current.set(numericId as number, el);
              else bubbleRefs.current.delete(numericId as number);
            }}
          >
            <MessageBubble
              message={msg}
              isStreaming={showStreaming}
              audioAnswerStreaming={audioAnswerStreaming}
              isLastMessage={isLast}
              onRetry={onRetry}
              onDelete={onDelete}
              onSendMessage={onSendMessage}
              originalUserText={originalUserText}
              onStartCall={onStartCall}
              sessionId={sessionId}
              forkGroups={forkGroups}
              activeBranchId={activeBranchId}
              onSwitchBranch={onSwitchBranch}
              onMergeBranch={onMergeBranch}
              mergingBranchId={mergingBranchId}
              onSaveAsDocument={onSaveAsDocument}
              actionRunning={actionRunning}
              disableIdentityLink={disableIdentityLink}
            />
            {/* FRONT-855: sits directly under the just-completed tool card,
                exactly where the answer text will land once it starts
                streaming — replaced the instant the first token arrives
                because isComposingAfterTool then sees non-empty text. */}
            {isLast && isComposing && <AnswerSkeleton />}
          </div>
        );
      })}
      {isWaiting && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[var(--color-chat-agent)] rounded-xl rounded-bl-sm w-fit">
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
            <span className="size-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
            <span className="size-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
          </div>
          <span className="text-sm text-muted-foreground">Thinking…</span>
        </div>
      )}
      {/* FRONT-1113: a tier-3 tool call blocks server-side, so the thread only
          shows "working" while the question itself used to sit in the Inspector
          side panel. Render this chat's own pending approvals here, at the end
          of the conversation that is waiting on them. */}
      <PendingApprovalCards sessionId={sessionId} />
      {/* FRONT-1194: the compute budget is spent, so the next turn would be
          refused before it starts. Say so in the thread rather than letting the
          person send into a void. */}
      <BudgetBlockedCard />
      <div ref={bottomRef} />
      {showJumpPill && (
        <div className="sticky bottom-3 self-center pointer-events-none">
          <Button
            type="button"
            size="sm"
            variant="default"
            onClick={() => {
              setPinned(true);
              scrollToBottom();
            }}
            className="pointer-events-auto shadow-md gap-1.5"
          >
            <ArrowDown size={14} weight="bold" /> Jump to latest
          </Button>
        </div>
      )}
    </div>
  );
}
